import { useRef } from 'react';
import { useField } from 'formik';
import { UploadOutlined } from '@ant-design/icons';
import { notification } from 'antd';
import {
    downloadIrelandH1ImportDeclarationSubmissionDetails,
    getBulkUploadPresignedUrl,
} from 'store/declarations/client';
import CsvIconOutlined from 'components/icons/CsvIconOutlined';
import Button from 'components/new-ui/base/button/Button';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import BulkUploadComponent, {
    BulkUploadRef,
    BulkUploadUploadFunc,
} from 'components/new-ui/composed/bulkUpload/BulkUpload';
import { NewShipmentFormFields, Steps } from '../../../types';
import { StyledFormSectionContentWrapper } from '../../../styles';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import { uploadFile } from 'utils/uploadFile';
import { FileTypes } from 'utils/file-types';

const BulkSubmission = () => {
    const fieldName = 'bulkSubmission.s3Link';

    const { isIdle, isComplete, isActive, currentStepConfig, resetStep } =
        useMultiStepFormContext<NewShipmentFormFields>();
    const [field, meta, helpers] = useField(fieldName);
    const uploaderRef = useRef<BulkUploadRef>(null);
    const fileName = field.value ? field.value.split('/')[1] : '';
    const disabled = currentStepConfig?.disabled ?? false;
    const stepKey = Steps.BULK_SUBMISSION;

    const handleUpload: BulkUploadUploadFunc = async (options: Parameters<BulkUploadUploadFunc>[0]) => {
        const file = options.file as File;

        const preSignUrlPayload = {
            fileSize: file.size,
            fileName: file.name,
        };

        try {
            const preSignedUrl = await getBulkUploadPresignedUrl(preSignUrlPayload);

            if (!preSignedUrl) {
                throw new Error('Could not get presigned URL');
            }

            try {
                await uploadFile(file, preSignedUrl);
            } catch (err) {
                console.log(err);
            }

            const pathname = new URL(preSignedUrl).pathname;
            const parts = pathname.split('/');
            const id = parts[1];
            const fileName = parts[2];

            if (!id || !fileName) {
                throw new Error('Could not generate s3 link');
            }

            const s3Link = id.concat('/').concat(fileName);

            if (s3Link) {
                helpers.setValue(s3Link, true);
                // setFileName(file.name);
                notification.success({
                    message: 'Files uploaded successfully',
                    placement: 'top',
                });
            } else {
                notification.error({
                    message: 'Files are not uploaded successfully. Please try again.',
                });
            }
        } catch (error) {
            console.error(error);
            return false;
        }

        return true;
    };

    const handleRemoveFile = () => {
        helpers.setValue(null, true);
    };

    const handleDownloadTemplate = () => {
        return downloadIrelandH1ImportDeclarationSubmissionDetails();
    };

    const handleUploadClick = () => {
        uploaderRef.current?.triggerUpload();
    };

    const handleReset = () => {
        resetStep(stepKey, 's3Link');
    };

    if (isIdle(Steps.BULK_SUBMISSION)) {
        return <FormSectionHeader title="Bulk Submission" icon={<CsvIconOutlined />} />;
    }

    if (isComplete(Steps.BULK_SUBMISSION)) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <FormSectionHeader title="Bulk Submission" icon={<CsvIconOutlined />} />
                <ReadonlyFormItem disabled={disabled} onReset={handleReset} value={fileName} />
            </div>
        );
    }

    if (isActive(Steps.BULK_SUBMISSION)) {
        return (
            <StyledFormSectionContentWrapper>
                <FormSectionHeader
                    title="Bulk Submission"
                    icon={<CsvIconOutlined />}
                    subtitle="Upload your spreadsheet in .xlsx format"
                    button={
                        <Button disabled={disabled} onClick={handleUploadClick} icon={<UploadOutlined />}>
                            Upload
                        </Button>
                    }
                />
                <BulkUploadComponent
                    ref={uploaderRef}
                    fileType={[FileTypes.XLSX]}
                    onUpload={handleUpload}
                    onDelete={handleRemoveFile}
                    onDownload={handleDownloadTemplate}
                    disabled={disabled}
                />
            </StyledFormSectionContentWrapper>
        );
    }

    return null;
};

export default BulkSubmission;
